// components/CustomerDetailsForm.tsx
import React from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  // Autocomplete,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { CustomerDetailFormState, ValidationErrors, CareHome } from '../types/customer';
import { elevation } from '../theme';
import { PhoneNumbersSection } from './PhoneNumbersSection';
import { TITLE_OPTIONS } from '../config/api';
import { isValid } from "date-fns";

interface CustomerDetailsFormProps {
  formData: CustomerDetailFormState;
  validationErrors: ValidationErrors;
  dobInputTempValue: Date | null;
  careHomes: CareHome[];
  loadingCareHomes: boolean;
  hasNoEmailChecked: boolean;
  hasNoPhoneChecked: boolean;
  phoneValidationErrors: (string | undefined)[];
  disabled?: boolean;
  onInputChange: (e: any) => void;
  onDobChange: (newValue: Date | null) => void;
  onDobAccept: (newValue: Date | null) => void;
  onDobClose: () => void;
  onCareHomeChange: (newValue: any) => void;
  onCareHomeInputChange: (value: string) => void;
  onHasNoEmailCheckedChange: (event: any) => void;
  onPhonesChange: (phones: any[]) => void;
  onPhoneValidationErrorsChange: (errors: (string | undefined)[]) => void;
  onHasNoPhoneCheckedChange: (checked: boolean) => void;
}

export const CustomerDetailsForm: React.FC<CustomerDetailsFormProps> = ({
  formData,
  validationErrors,
  dobInputTempValue,
  careHomes,
  loadingCareHomes,
  hasNoEmailChecked,
  hasNoPhoneChecked,
  phoneValidationErrors,
  disabled = false,
  onInputChange,
  onDobChange,
  onDobAccept,
  onDobClose,
  onCareHomeChange,
  onCareHomeInputChange,
  onHasNoEmailCheckedChange,
  onPhonesChange,
  onPhoneValidationErrorsChange,
  onHasNoPhoneCheckedChange,
}) => {
  // const careHomeValue = Array.isArray(careHomes)
  //   ? careHomes.find((ch) => ch.externalId === formData.careHomeExternalId) || null
  //   : null;

  return (
    <Paper sx={{ p: 3, height: "100%" }} elevation={elevation.paper}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Customer Details
      </Typography>
      <Box
        component="form"
        noValidate
        autoComplete="off"
        display="grid"
        gap={3}
        gridTemplateColumns="repeat(12, 1fr)"
      >
        {/* First row: Title, First Name, Last Name */}
        <Box gridColumn={{ xs: "span 12", sm: "span 3" }}>
          <Tooltip title="As per official identification document">
          <FormControl fullWidth>
            <InputLabel id="title-label">Title</InputLabel>
            <Select
              labelId="title-label"
              name="title"
              value={formData.title || ""}
              label="Title"
              onChange={onInputChange}
              error={Boolean(validationErrors.title)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {TITLE_OPTIONS.map((title) => (
                <MenuItem key={title} value={title}>
                  {title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          </Tooltip>
        </Box>
        <Box gridColumn={{ xs: "span 12", sm: "span 4" }}>
          <Tooltip title="As per official identification document">
          <TextField
            fullWidth
            required
            label="First Name"
            name="firstName"
            value={formData.firstName || ""}
            onChange={onInputChange}
            disabled={disabled}
            error={Boolean(validationErrors.firstName)}
            helperText={validationErrors.firstName}
          />
          </Tooltip>
        </Box>
        <Box gridColumn={{ xs: "span 12", sm: "span 5" }}>
          <Tooltip title="As per official identification document">
          <TextField
            fullWidth
            required
            label="Last Name"
            name="lastName"
            value={formData.lastName || ""}
            onChange={onInputChange}
            disabled={disabled}
            error={Boolean(validationErrors.lastName)}
            helperText={validationErrors.lastName}
          />
          </Tooltip>
        </Box>

        {/* Second row: Date of Birth, Gender, Shared Space */}
        <Box gridColumn={{ xs: "span 12", sm: "span 7" }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Date of Birth"
              minDate={new Date("1900-01-01")}
              maxDate={new Date()}
              value={dobInputTempValue}
              onChange={(newValue) => onDobChange(newValue as Date)}
              onAccept={(newValue) => {
                if (newValue instanceof Date && isValid(newValue)) {
                  onDobAccept(newValue);
                }
              }}
              onClose={() => {
                if (
                  dobInputTempValue instanceof Date &&
                  isValid(dobInputTempValue)
                ) {
                  onDobClose();
                }
              }}
              format="dd/MM/yyyy"
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  error: Boolean(validationErrors.dateOfBirth),
                  helperText: validationErrors.dateOfBirth,
                  disabled: disabled,
                  inputProps: {
                    style: { fontVariantNumeric: "tabular-nums" },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Box>
        <Box gridColumn={{ xs: "span 12", sm: "span 5" }}>
          <FormControl fullWidth>
            <InputLabel id="gender-label">Gender</InputLabel>
            <Select
              labelId="gender-label"
              name="patientGenderCode"
              value={formData.patientGenderCode || ""}
              label="Gender"
              onChange={onInputChange}
              disabled={disabled}
              error={Boolean(validationErrors.patientGenderCode)}
            >
              <MenuItem value="M">Male</MenuItem>
              <MenuItem value="F">Female</MenuItem>
              <MenuItem value="X">Other</MenuItem>
            </Select>
          </FormControl>
        </Box>
         {/*<Box gridColumn={{ xs: "span 12", sm: "span 5" }}>
          <Autocomplete
            options={careHomes}
            getOptionLabel={(option) => option.name}
            value={careHomeValue}
            onChange={(_, newValue) => onCareHomeChange(newValue)}
            onInputChange={(_, value, reason) => {
              if (reason === "input") {
                onCareHomeInputChange(value);
              }
            }}
            loading={loadingCareHomes}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Shared Address"
                error={Boolean(validationErrors.careHomeExternalId)}
                helperText={validationErrors.careHomeExternalId}
              />
            )}
            isOptionEqualToValue={(option, value) =>
              option.externalId === value.externalId
            }
            noOptionsText="No shared addresses found"
            clearOnBlur={false}
            filterOptions={(options) => options}
          /> 
        </Box>*/}

        {/* Third row: Email */}
        <Box gridColumn={{ xs: "span 12", sm: "span 7" }}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            disabled={hasNoEmailChecked || disabled}
            value={formData.email || ""}
            onChange={onInputChange}
            error={Boolean(validationErrors.email)}
            helperText={validationErrors.email}
          />
        </Box>
        <Box gridColumn={{ xs: "span 12", sm: "span 5" }}>
          <FormControlLabel
            label="Customer has no email"
            sx={{ mt: 1 }}
            control={
              <Checkbox
                checked={hasNoEmailChecked}
                onChange={onHasNoEmailCheckedChange}
                disabled={disabled}
              />
            }
          />
        </Box>

        {/* Phone Numbers section */}
        <PhoneNumbersSection
          phones={formData.phones}
          phoneValidationErrors={phoneValidationErrors}
          validationErrors={validationErrors}
          hasNoPhoneChecked={hasNoPhoneChecked}
          disabled={disabled}
          onPhonesChange={onPhonesChange}
          onPhoneValidationErrorsChange={onPhoneValidationErrorsChange}
          onHasNoPhoneCheckedChange={onHasNoPhoneCheckedChange}
        />
      </Box>
    </Paper>
  );
};